import type {
  PageWithLayout,
  PageProps,
  IFilterOutput,
  ISortOutput,
  IProductList,
  IFilterAndSortInput,
} from "@types";
import type { Meta } from "@layouts/page";
import type { GetServerSideProps } from "next";

import Page from "@layouts/page";
import { useTranslations } from "use-intl";
import { getCookie, setContext } from "@utils/Cookies";
import { getLayout as getDefaultLayout } from "@layouts/default-layout";
import Product from "@templates/product";
import ProductApi from "@api/productApi";
import Loading from "assets/lottie/loading";
import { parse } from "next-useragent";
import { setCountryAndArea } from "@utils/checkDependencies";
import { COOKIE_ACCESS_TOKEN_KEY } from "@utils/constants";
import { authApi } from "gate";
import { checkIfMaintenanceNeededOrFail } from "@utils/api";

interface Props extends PageProps {
  filter?: IFilterOutput | null;
  sort?: ISortOutput | null;
  search?: string;
  clientFilter: IFilterOutput[];
  secondaryFilter: IFilterOutput[];
  data?: IProductList;
  filterAndSort?: IFilterAndSortInput | null;
}

const ProductPage: PageWithLayout<Props> = ({
  filter,
  sort,
  search,
  clientFilter,
  secondaryFilter,
  appFallback,
  data,
  filterAndSort
}) => {
  const t = useTranslations("");
  for (const iterator of clientFilter) {
    let data = { ...iterator };
    delete data.id;
  }

  if (!data) {
    return null
  }

  const meta: Meta = {
    title: t("client_productlist_productlist"),
    description: appFallback?.website.description || "Dabdoob",
    keywords: appFallback?.website.keywords
  };

  return (
    <Page meta={meta}>
      <Product
        serverFilter={filter || undefined}
        clientFilter={clientFilter}
        serverSearch={search}
        serverSort={sort || undefined}
        data={data}
        clientSecondaryFilter={secondaryFilter}
        filters={filterAndSort || undefined}
      />
    </Page>
  );
};

ProductPage.translationFile = "translation";
ProductPage.getLayout = (page, props) =>
  getDefaultLayout(page, { ...props }, {});

export const getServerSideProps: GetServerSideProps<Props> = async (ctx) => {
  const { query } = ctx;
  setContext(ctx);
  const ssr = typeof query.ssr === "string" ? query.ssr : "";
  const ua = parse(ctx.req.headers['user-agent'] || "")
  const Cookies = getCookie();

  if (!Cookies.get(COOKIE_ACCESS_TOKEN_KEY)) {
    await authApi
      .deviceAuth()
      .fetch()
      .then((res) => {
        const token = res.data.access_token || "";
        Cookies.set(COOKIE_ACCESS_TOKEN_KEY, token, {}, 365 * 2);
      })
      .catch((err) => {
        console.log(err);
      });
  }
  if ((ssr === "1" || ua.isBot)) {
    await setCountryAndArea(ctx.locale)
  }
  const category =
    typeof query.category_id === "string" ? query.category_id : "";
  const subcategory =
    typeof query.subcategory_id === "string" ? query.subcategory_id : "";
  const attribute =
    typeof query.attributes_id === "string" ? query.attributes_id : "";
  const brand = typeof query.brand_id === "string" ? query.brand_id : "";
  const tag = typeof query.tags_id === "string" ? query.tags_id : "";
  const collection =
    typeof query.collection_id === "string" ? query.collection_id : "";
  const input = typeof query.input === "string" ? query.input : "";
  const sortKey = typeof query.sortKey === "string" ? query.sortKey : "";
  const sortValue = typeof query.sortValue === "string" ? query.sortValue : "";
  const clientFilters =
    typeof query.filters === "string" ? JSON.parse(query.filters) : [];
  const secondaryFilters =
    typeof query.secondaryFilters === "string"
      ? JSON.parse(query.secondaryFilters)
      : [];

  let filter: IFilterOutput | undefined;
  if (category) {
    filter = {
      key: "category_id",
      type: "singleSelect",
      operation_type: "include",
      items: [parseInt(category)],
      items_operation: "or",
    };
  } else if (!category && subcategory) {
    filter = {
      key: "subcategory_id",
      type: "singleSelect",
      operation_type: "include",
      items: [parseInt(subcategory)],
      items_operation: "or",
    };
  } else if (attribute) {
    filter = {
      key: "attributes_id",
      type: "singleSelect",
      operation_type: "include",
      items: [parseInt(attribute)],
      items_operation: "or",
    };
  } else if (brand) {
    filter = {
      key: "brand_id",
      type: "singleSelect",
      operation_type: "include",
      items: [parseInt(brand)],
      items_operation: "or",
    };
  } else if (tag) {
    filter = {
      key: "tags_id",
      type: "singleSelect",
      operation_type: "include",
      items: [parseInt(tag)],
      items_operation: "or",
    };
  } else if (collection) {
    filter = {
      key: "collection_id",
      type: parseInt(collection) ? "singleSelect" : "custom",
      operation_type: "include",
      items: parseInt(collection) ? [parseInt(collection)] : [],
      items_operation: "or",
      custom: parseInt(collection) ? undefined : collection,
    };
  } else {
    filter = undefined;
  }


  if (collection) {
    if (parseInt(collection)) {
      delete filter?.custom;
    } else {
      delete filter?.items;
    }
  }
  const search = input ? { search: input } : {};
  const sort =
    sortKey && sortValue
      ? { sort: { key: sortKey, value: parseInt(sortValue) as any } }
      : {};
  try {
    const _filters: IFilterOutput[] = [];
    const _secondaryFilters: IFilterOutput[] = [];
    const sortAndFilters = await ProductApi.sortAndFilter().fetch();
    const serverFilters = sortAndFilters.data.filters;

    for (const filter of clientFilters) {
      for (const [idx, iterator] of serverFilters.entries()) {
        if (iterator.key === filter.filterKey) {
          if (
            filter.filterKey === "brand_id" ||
            filter.filterKey === "category_id" ||
            filter.filterKey === "subcategory_id" ||
            filter.filterKey === "tags_id"
          ) {
            _filters.push({
              key: filter.filterKey,
              type: iterator.type,
              operation_type: iterator.operation_type,
              items:
                filter.filterValue.split(",").map((el: string) => {
                  let n = Number(el);
                  return n === 0 ? n : n || el;
                }) || undefined,
              items_operation: iterator.items_operation,
              id: idx + 1,
            });
          }
          if (filter.filterKey === "attributes_id") {
            iterator.items?.map((e) => {
              if (e.value === Number(filter.filterValue)) {
                _filters.push({
                  key: filter.filterKey,
                  type: iterator.type,
                  operation_type: iterator.operation_type,
                  items:
                    filter.filterValue.split(",").map((el: string) => {
                      let n = Number(el);
                      return n === 0 ? n : n || el;
                    }) || undefined,
                  items_operation: iterator.items_operation,
                  id: idx + 1,
                });
              }
            });
          }
          if (filter.filterKey === "sku_price") {
            _filters.push({
              key: filter.filterKey,
              type: iterator.type,
              operation_type: iterator.operation_type,
              items:
                filter.filterValue.split(",").map((el: string) => {
                  let n = Number(el);
                  return n === 0 ? n : n || el;
                }) || undefined,
              items_operation: iterator.items_operation,
              id: idx + 1,
            });
          }
        }
      }
    }

    for (const filter of secondaryFilters) {
      if (filter.filterKey === "subcategory_id") {
        _secondaryFilters.push({
          key: "subcategory_id",
          operation_type: "include",
          type: "singleSelect",
          items:
            filter.filterValue.split(",").map((el: string) => {
              let n = Number(el);
              return n === 0 ? n : n || el;
            }) || undefined,
        });
      }
      for (const iterator of serverFilters) {
        if (iterator.key === filter.filterKey) {
          if (
            filter.filterKey === "brand_id" ||
            filter.filterKey === "category_id" ||
            filter.filterKey === "tags_id"
          ) {
            _secondaryFilters.push({
              key: filter.filterKey,
              type: iterator.type,
              operation_type: iterator.operation_type,
              items:
                filter.filterValue.split(",").map((el: string) => {
                  let n = Number(el);
                  return n === 0 ? n : n || el;
                }) || undefined,
            });
          }
          if (filter.filterKey === "attributes_id") {
            iterator.items?.map((e) => {
              if (e.value === Number(filter.filterValue)) {
                _secondaryFilters.push({
                  key: filter.filterKey,
                  type: iterator.type,
                  operation_type: iterator.operation_type,
                  items:
                    filter.filterValue.split(",").map((el: string) => {
                      let n = Number(el);
                      return n === 0 ? n : n || el;
                    }) || undefined,
                });
              }
            });
          }
          if (filter.filterKey === "sku_price") {
            _secondaryFilters.push({
              key: filter.filterKey,
              type: iterator.type,
              operation_type: iterator.operation_type,
              items:
                filter.filterValue.split(",").map((el: string) => {
                  let n = Number(el);
                  return n === 0 ? n : n || el;
                }) || undefined,
              items_operation: iterator.items_operation,
            });
          }
        }
      }
    }

    for (const iterator of _filters) {
      let data = { ...iterator };
      delete data.id;
    }

    const data = await ProductApi.list({
      filters: filter ? [{ ...filter }] : undefined,
      ...search,
      ...sort,
    }).fetch()
    return {
      props: {
        data: data.data,
        filter: filter ? filter : null,
        search: search?.search ?? "",
        sort: sort?.sort ?? null,
        clientFilter: [..._filters],
        secondaryFilter: [..._secondaryFilters],
        filterAndSort: sortAndFilters?.data
      },
    };
  } catch (error) {
    return checkIfMaintenanceNeededOrFail(error, {
      props: {
        filter: filter ? filter : null,
        search: search?.search ?? "",
        sort: sort?.sort ?? null,
        clientFilter: [],
        secondaryFilter: [],
        filterAndSort: null
      },
    });
  }
};

export default ProductPage;
